/**
 * This file contains all methods that we don't want to directly expose as part of the AudioPlayback library.
 * In some ways you can think of them as the "private" or "protected" methods of the AudioPlayback library.
 */

import * as AudioConstants from '../../constants';

import AudioPlaybackProvider from './AudioPlaybackProvider';

// TODO: currently you can only have as many harnesses as maxProviders, decouple that
const defaultOptions = {
  maxProviders: 5,
};

const AudioPlaybackProtected = {
  /**
   *
   * @param {AudioPlaybackOptions} options
   * @returns {object}
   */
  initOptions(options) {
    const logger = options.logger;
    if (logger) {
      AudioPlaybackProvider.setLogger(options.logger);
    }

    return Object.assign({}, defaultOptions, options);
  },

  /**
   *
   * @param {AudioPlaybackState} audioPlaybackState
   * @param {string} audioUrl
   * @returns {Promise}
   */
  destroyAndCleanupProvider(audioPlaybackState, audioUrl) {
    return new Promise((resolve, reject) => {
      const activityStackIndex = audioPlaybackState.activityStack.indexOf(audioUrl);
      const providerRequest = audioPlaybackState.providerRequests.get(audioUrl);

      if (providerRequest) {
        audioPlaybackState.providerRequests.delete(audioUrl);
        providerRequest
          .then(provider => {
            provider._destroy();
            resolve();
          })
          .catch(reject);
      } else {
        resolve();
      }

      if (activityStackIndex >= 0) {
        audioPlaybackState.activityStack.splice(activityStackIndex, 1);
      }
    });
  },

  /**
   *
   * @param {AudioPlaybackState} audioPlaybackState
   * @param {number} minProvidersToKeep
   * @returns {Promise}
   */
  deleteOldestProviders(audioPlaybackState, minProvidersToKeep = 0) {
    const maxProviders = audioPlaybackState.options.maxProviders;
    const numProviders = audioPlaybackState.activityStack.length;
    const audioUrlsToDestroy = [];
    if (numProviders >= maxProviders) {
      const providersToKeep = Math.max(maxProviders - 1, minProvidersToKeep);
      const providersToDestroy = numProviders - providersToKeep;
      for (let i = 0; i < providersToDestroy; i++) {
        const audioUrl = audioPlaybackState.activityStack[i];
        audioUrlsToDestroy.push(audioUrl);
      }
    }

    // sequentially composing all audioUrlsToDestroy so that we only destroy one provider at a time.
    return audioUrlsToDestroy.reduce((p, audioUrl) => {
      return p.then(() => {
        return AudioPlaybackProtected.destroyAndCleanupProvider(audioPlaybackState, audioUrl);
      });
    }, Promise.resolve());
  },

  /**
   *
   * @param {AudioPlaybackState} audioPlaybackState
   * @param {AudioSrc} audioSrc
   * @returns {HarnessConfig}
   */
  getHarnessConfigForAudioSource(audioPlaybackState, audioSrc) {
    return audioPlaybackState.options.harnessConfigs.find(config => {
      const isUnencrypted = !audioSrc.key;
      if (
        config.name === AudioConstants.AudioHarnessName.SXM_AUDIO_PLAYER &&
        isUnencrypted &&
        !(
          audioPlaybackState.options.audioData.isSingleHarnessForStationAndAudioAdsEnabled ||
          (audioSrc.audioType === AudioConstants.AudioTypes.PODCAST &&
            audioPlaybackState.options.audioData.isPodcastsOnSxmPlayerEnabled) ||
          audioPlaybackState.options.audioData.isSxmUnencryptedMediaelementPlayback2021Enabled ||
          audioPlaybackState.options.audioData.isSxmUnencryptedWAAPlayback2021Enabled ||
          audioPlaybackState.options.audioData.isSxmEncryptedStationFetchDownloaderEnabled ||
          audioPlaybackState.options.audioData.isSxmEncryptedStationFetchDownloaderV2Enabled
        )
      ) {
        return false;
      }

      if (!Array.isArray(config.formats) || config.formats.indexOf(audioSrc.format) === -1) {
        return false;
      }

      if (Array.isArray(config.audioTypes)) {
        return config.audioTypes.indexOf(audioSrc.audioType) > -1;
      }

      return true;
    });
  },

  /**
   *
   * @param {AudioPlaybackState} audioPlaybackState
   * @param {string} harnessName
   * @returns {Harness}
   */
  getHarnessByName(audioPlaybackState, harnessName) {
    return audioPlaybackState.harnesses.find(harness => {
      return harness.NAME === harnessName;
    });
  },

  /**
   *
   * @param {AudioPlaybackState} audioPlaybackState
   * @param {AudioSrc} audioSrc
   * @returns {Harness}
   */
  getHarnessForAudioSource(audioPlaybackState, audioSrc) {
    const harnessConfig = AudioPlaybackProtected.getHarnessConfigForAudioSource(
      audioPlaybackState,
      audioSrc,
    );

    if (!harnessConfig) {
      throw new Error(
        `No harness config for audioSrc: format = ${audioSrc.format}, audioType = ${audioSrc.audioType}`,
      );
    }

    const harness = AudioPlaybackProtected.getHarnessByName(audioPlaybackState, harnessConfig.name);

    if (!harness) {
      throw new Error(`No harness matches for harness name: ${harnessConfig.name}`);
    }

    return harness;
  },

  /**
   *
   * @param {Harness} harness
   * @param {AudioSrc} audioSrc
   * @param {object} options
   * @returns {Promise}
   */
  createAudioPlayer(harness, audioSrc, options) {
    return new Promise((resolve, reject) => {
      harness.initialize(audioSrc, resolve, reject, options);
    });
  },

  /**
   *
   * @param {Harness} harness
   * @param {AudioSrc} audioSrc
   * @param {object} options
   * @returns {Promise.<AudioProvider>}
   */
  createAudioProviderFromHarness(harness, audioSrc, options) {
    return new Promise((resolve, reject) => {
      AudioPlaybackProtected.createAudioPlayer(harness, audioSrc, options)
        .then(player => {
          const playerMethods = harness.mapMethods(player, options);
          const eventBinders = harness.mapEventBindings(player);
          const eventRemovers = harness.mapEventRemovers(player);
          const audioProvider = AudioPlaybackProvider.createAudioProvider(
            harness.NAME,
            playerMethods,
            eventBinders,
            eventRemovers,
          );
          audioProvider.player = player;
          resolve(audioProvider);
        })
        .catch(reject);
    });
  },

  /**
   *
   * @param {AudioPlaybackState} audioPlaybackState
   * @param {AudioSrc} audioSrc
   * @param {object} options
   * @returns {Promise.<AudioProvider>}
   */
  createAudioProvider(audioPlaybackState, audioSrc, options) {
    return new Promise((resolve, reject) => {
      const harness = AudioPlaybackProtected.getHarnessForAudioSource(audioPlaybackState, audioSrc);
      const providerRequest = AudioPlaybackProtected.createAudioProviderFromHarness(
        harness,
        audioSrc,
        options,
      );
      const audioUrl = audioSrc.url;

      audioPlaybackState.providerRequests.set(audioUrl, providerRequest);
      audioPlaybackState.activityStack.push(audioUrl);

      return providerRequest.then(resolve).catch(reject);
    });
  },

  /**
   *
   * @param {AudioPlaybackState} audioPlaybackState
   * @param {AudioSrc} audioSrc
   * @param {Function} defaultError
   * @param {boolean} isPreload
   * @returns {Promise.<AudioProvider>}
   */
  requestNewAudioProvider(audioPlaybackState, audioSrc, defaultError, isPreload) {
    return new Promise((resolve, reject) => {
      const options = audioPlaybackState.options;
      const maxProviders = options.maxProviders;

      // Setting maxProviders to `1` signals to the harness to reuse resources between instances
      if (maxProviders === 1) {
        options.reuseElements = true;
      }

      // Setting maxProviders to falsy disables any maximum limit
      if (maxProviders && audioPlaybackState.providerRequests.size >= maxProviders) {
        return AudioPlaybackProtected.deleteOldestProviders(audioPlaybackState, isPreload ? 1 : 0)
          .then(() => {
            return AudioPlaybackProtected.createAudioProvider(
              audioPlaybackState,
              audioSrc,
              Object.assign(options, {
                defaultError,
                isPreload,
              }),
            )
              .then(resolve)
              .catch(reject);
          })
          .catch(reject);
      }

      return AudioPlaybackProtected.createAudioProvider(
        audioPlaybackState,
        audioSrc,
        Object.assign(options, { defaultError, isPreload }),
      )
        .then(resolve)
        .catch(reject);
    });
  },

  /**
   *
   * @param {AudioPlaybackState} audioPlaybackState
   * @param {AudioSrc} audioSrc
   * @param {Function} defaultError
   * @param {boolean} isPreload
   * @returns {Promise.<AudioProvider>}
   */
  getAudioProvider(audioPlaybackState, audioSrc, defaultError, isPreload) {
    return new Promise((resolve, reject) => {
      const audioUrl = audioSrc.url;
      const providerRequest = audioPlaybackState.providerRequests.get(audioUrl);

      if (providerRequest) {
        // Remove the provider from its current place in the stack, then add it back to the top.
        audioPlaybackState.activityStack.splice(
          audioPlaybackState.activityStack.indexOf(audioUrl),
          1,
        );
        audioPlaybackState.activityStack.push(audioUrl);
        resolve(providerRequest);
      } else {
        AudioPlaybackProtected.requestNewAudioProvider(
          audioPlaybackState,
          audioSrc,
          defaultError,
          isPreload,
        )
          .then(resolve)
          .catch(reject);
      }
    });
  },

  updateUrl(audioPlaybackState, oldUrl, newUrl) {
    return new Promise((resolve, reject) => {
      const providerRequest = audioPlaybackState.providerRequests.get(oldUrl);
      if (providerRequest) {
        providerRequest
          .then(provider => {
            provider.updateUrl(newUrl);
            audioPlaybackState.activityStack.splice(
              audioPlaybackState.activityStack.indexOf(oldUrl),
              1,
            );
            audioPlaybackState.activityStack.push(newUrl);
            audioPlaybackState.providerRequests.set(newUrl, providerRequest);
            audioPlaybackState.providerRequests.delete(oldUrl);
            resolve();
          })
          .catch(e => {
            reject(e);
          });
      } else {
        reject(new Error('Provider to update cannot be found.'));
      }
    });
  },
};

export default AudioPlaybackProtected;
